import { useCallback, useRef } from 'react';

interface UseScrollToElementProps {
  setIsProgrammaticScroll: (value: boolean) => void;
  headerHeight: number;
}

export const useScrollToElement = ({
  setIsProgrammaticScroll,
  headerHeight,
}: UseScrollToElementProps) => {
  const scrollTimeoutRef = useRef<number | null>(null);
  const scrollListenerRef = useRef<(() => void) | null>(null);

  const removeScrollListener = useCallback(() => {
    if (scrollListenerRef.current) {
      window.removeEventListener('scroll', scrollListenerRef.current);
      scrollListenerRef.current = null;
    }
    if (scrollTimeoutRef.current !== null) {
      clearTimeout(scrollTimeoutRef.current);
      scrollTimeoutRef.current = null;
    }
  }, []);

  return useCallback(
    (elementId: string, extraPadding: number = 0) => {
      removeScrollListener();
      setIsProgrammaticScroll(true);
      const element = document.getElementById(elementId);
      if (element) {
        const elementPosition = element.getBoundingClientRect().top;
        const offsetPosition =
          elementPosition + window.pageYOffset - headerHeight - extraPadding;

        window.scrollTo({
          top: offsetPosition,
          behavior: 'smooth',
        });

        let lastKnownScrollPosition = window.pageYOffset;
        let ticking = false;

        const handleScroll = () => {
          lastKnownScrollPosition = window.pageYOffset;
          if (!ticking) {
            window.requestAnimationFrame(() => {
              const currentPosition = window.pageYOffset;
              if (Math.abs(currentPosition - offsetPosition) < 2) {
                setIsProgrammaticScroll(false);
                removeScrollListener();
              } else if (
                Math.abs(currentPosition - lastKnownScrollPosition) < 1
              ) {
                // If scroll has stopped (user interrupted or scroll completed)
                if (scrollTimeoutRef.current !== null) {
                  clearTimeout(scrollTimeoutRef.current);
                }
                scrollTimeoutRef.current = window.setTimeout(() => {
                  setIsProgrammaticScroll(false);
                  removeScrollListener();
                }, 100);
              }
              ticking = false;
            });
            ticking = true;
          }
        };

        scrollListenerRef.current = handleScroll;
        window.addEventListener('scroll', handleScroll, { passive: true });
      }
    },
    [setIsProgrammaticScroll, removeScrollListener, headerHeight],
  );
};
